import { useEffect, useMemo, useState } from 'react';
import { getCoreRowModel } from '@tanstack/react-table';

// MUI Theme
import { Paper } from '@mui/material';
import { makeStyles } from '@mui/styles';

// Utils
import { makeSuperSentinelBulkActivationColumn } from '../../../../utils/columns/makeSuperSentinelBulkActivationColumn';
import useDateTimeFormat from '../../../../hooks/useDateTimeFormat';

// Services
import { getSuperSentinelImportDetails } from '../../../../services/superSentinel.services';

// Components
import { Table, useStandardTable as useTable } from '../../../common/table';

// Types
import { InventoryContent } from '../../../../constants/Constants';

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '70%',
    marginBottom: '10px',
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '22px',
  },
  heading: {
    margin: '0 0 10px',
  },
  content: {
    paddingLeft: '8px',
    fontSize: '12px',
  },
}));

const SuperSentinelBulkActivationDetails = ({ importId }: { importId: string }) => {
  const { toStandardDate, withTimeMode } = useDateTimeFormat();
  const classes = useStyles();

  const [detail, setDetail] = useState<any>(null);

  const { statusSummary, createdDateTime, totalDevices, userName } = detail ?? {};

  /**
   * Pending count fix:
   *
   * The API may return statusSummary.pending as 0 even when devices are still
   * unaccounted for.  We derive the real pending count from the total so the
   * summary always adds up:
   *
   *   pending = total − processed − processing − error
   *
   * We clamp to 0 to handle any edge cases where the server numbers are
   * temporarily inconsistent.
   */
  const derivedPending = useMemo(() => {
    if (!statusSummary || !totalDevices) return 0;
    const { processed = 0, processing = 0, error = 0 } = statusSummary;
    return Math.max(0, totalDevices - processed - processing - error);
  }, [statusSummary, totalDevices]);

  const columns = useMemo(() => makeSuperSentinelBulkActivationColumn(), []);

  const table = useTable<any>({
    columns,
    serverSide: false,
    shouldFetch: false,
  });

  useEffect(() => {
    if (!importId) return;

    let active = true;

    (async () => {
      const response: any = await getSuperSentinelImportDetails(importId)({});
      const data = response?.data ?? response;
      if (active) {
        setDetail(data);
        table.setData(data?.devices ?? []);
      }
    })();

    return () => {
      active = false;
    };
  }, [importId]);

  return (
    <>
      <h4 className={classes.heading}>Super Sentinel Bulk Activation Summary</h4>

      <div className={classes.container}>
        <div>
          <div className={classes.contentWrapper}>
            <h5>Total Devices</h5>
            <p className={classes.content}>{totalDevices ?? 0}</p>
          </div>
          <div className={classes.contentWrapper}>
            <h5>Processed</h5>
            <p className={classes.content}>{statusSummary?.processed ?? 0}</p>
          </div>
          <div className={classes.contentWrapper}>
            {/* Derived pending ensures the count reflects reality even when the
                API returns 0 while records are still being processed. */}
            <h5>Pending</h5>
            <p className={classes.content}>{derivedPending}</p>
          </div>
          <div className={classes.contentWrapper}>
            <h5>Processing</h5>
            <p className={classes.content}>{statusSummary?.processing ?? 0}</p>
          </div>
          <div className={classes.contentWrapper}>
            <h5>Errors</h5>
            <p className={classes.content}>{statusSummary?.error ?? 0}</p>
          </div>
        </div>

        <div>
          <div className={classes.contentWrapper}>
            <h5>{withTimeMode(InventoryContent.createdDateTime)}</h5>
            <p className={classes.content}>{toStandardDate(createdDateTime) ?? '-'}</p>
          </div>
          <div className={classes.contentWrapper}>
            <h5>User Name</h5>
            <p className={classes.content}>{userName ?? '-'}</p>
          </div>
        </div>
      </div>

      <h4 className={classes.heading}>Device Details</h4>

      <Paper>
        <Table {...table} showSearchBar={false} showColumnToggler={false} showAction={false} getCoreRowModel={getCoreRowModel()} />
      </Paper>
    </>
  );
};

export default SuperSentinelBulkActivationDetails;
